import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  useTheme,
  useMediaQuery,
  ListSubheader,
  Avatar,
  Menu
} from "@mui/material";
import {
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  Business as BusinessIcon,
  BarChart as BarChartIcon,
  Home as HomeIcon,
  CalendarMonth as CalendarIcon,
  Settings as CogsIcon,
  People as PeopleIcon,
  ShoppingCart as ShoppingCartIcon,
  AccessTime as AccessTimeIcon,
  TableChart,
  List as ListIcon,
  AccountCircle,
  EventAvailable,
  Badge,
  Inventory as InventoryIcon,
  Assessment as AssessmentIcon
} from "@mui/icons-material";
import { styled } from '@mui/material/styles';
import { Routes, Route } from 'react-router-dom';
import Home from '../pages/Home';
import TrackingListManagerView from '../pages/TrackingListManagerView';
import TrackingListUserView from '../pages/TrackingListUserView';
import DailyTracking from '../pages/DailyTracking';
import Customers from '../pages/Customers';
import Projects from '../pages/Projects';
import ProjectDetail from '../pages/ProjectDetail';

import Users from '../pages/Users';
import UserDetail from '../pages/UserDetail';
import Activities from '../pages/Activities';
import Profile from '../pages/Profile';
import RightSection from './RightSection';
import MonthlyManagerView from '../pages/MonthlyManagerView';
import MonthlyProjectDetail from '../pages/MonthlyProjectDetail';
import MonthlyUserView from '../pages/MonthlyUserView';
import MonthlyPeopleManagerView from '../pages/MonthlyPeopleManagerView';
import MonthlyPeopleManagerDetail from '../pages/MonthlyPeopleManagerDetail';
import TrackingListPeopleManagerView from '../pages/TrackingListPeopleManagerView';
import NotFound from '../pages/NotFound';
import Login from '../pages/Login';
import ProjectReporting from '../pages/ProjectReporting';
import ProtectedRoute from './ProtectedRoute';

import "../App.css";
import { createContext } from 'react';
import { API_ENDPOINTS } from '../config/api.config';
import { useCognito } from '../hooks/useCognito';
import { hasMenuSectionAccess, hasMenuItemAccess, getRoleDisplayName } from '../utils/roleManager';

export const UserContext = createContext();

const drawerWidth = 240;

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
  justifyContent: 'flex-end',
}));

export default function MiniDrawer() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [open, setOpen] = useState(() => !window.matchMedia('(max-width:600px)').matches);
  const [rightOpen, setRightOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Gestione autenticazione Cognito
  const { user: cognitoUser, backendUser, loading: authLoading, isAuthenticated, logout: cognitoLogout } = useCognito();

  const [users, setUsers] = useState([]);
  const [userLoading, setUserLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState('');

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    async function fetchUsers() {
      setUserLoading(true);
      try {
        const res = await fetch(API_ENDPOINTS.USERS);
        const data = await res.json();
        let arr = data.users || [];
        setUsers(Array.isArray(arr) ? arr : []);
        
        // In development mode, force selectedUser to 1
        if (process.env.NODE_ENV === 'development') {
          setSelectedUser(1);
        } else {
          // Se c'è un utente del backend autenticato, usalo come selectedUser
          if (backendUser && backendUser.id) {
            setSelectedUser(backendUser.id);
          } else {
            // Non impostare un utente di default se non c'è autenticazione
            // Questo evita di mostrare dati di altri utenti
            setSelectedUser('');
          }
        }
      } catch {
        setUsers([]);
      } finally {
        setUserLoading(false);
      }
    }
    fetchUsers();
  }, [backendUser]);

  const menuItems = [
    { text: "Home", icon: <HomeIcon />, path: "/" },
    // My Area
    { section: "My Area" },
    { text: "Daily Tracking", icon: <AccessTimeIcon />, path: "/daily-tracking" },
    { text: "Monthly View", icon: <CalendarIcon />, path: "/monthly-user-view" },
    { text: "List View", icon: <ListIcon />, path: "/tracking-list-user" },
    // Manager Area
    { section: "Manager Area" },
    { text: "Monthly Tracking View", icon: <EventAvailable />, path: "/monthly-mng-view" },
    { text: "List Tracking View", icon: <ListIcon />, path: "/list-tracking-view" },
    { separator: true }, 
    { text: "Projects", icon: <InventoryIcon />, path: "/projects" },
    { text: "Customers", icon: <Badge />, path: "/customers" },
    
    // People Manager Area - visible only to users with org_role_id = 2
    { section: "People Manager Area", requiresOrgRole: 2 },
    { text: "Monthly People Manager View", icon: <EventAvailable />, path: "/monthly-people-mng-view", requiresOrgRole: 2 },
    { text: "List People Manager View", icon: <ListIcon />, path: "/list-tracking-people-mng-view", requiresOrgRole: 2 },
    
    // Admin Area
    { section: "Admin Area" },
    { text: "Users", icon: <PeopleIcon />, path: "/users" },
    { text: "Activities", icon: <ListIcon />, path: "/activities" },
    { text: "Project Reporting", icon: <AssessmentIcon />, path: "/project-reporting" },
  ];

  // Filter menu items based on user role and org_role_id
  const getFilteredMenuItems = () => {
    if (!backendUser) {
      // In development mode, show all items
      if (process.env.NODE_ENV === 'development') {
        return menuItems;
      }
      // In production, show only Home if user is authenticated but not in database
      if (isAuthenticated && cognitoUser) {
        return [{ text: "Home", icon: <HomeIcon />, path: "/" }];
      }
      return []; // Hide all menu items if no user data
    }
    
    const userRole = backendUser.role_id;
    const orgRoleId = backendUser.org_role_id; // org_role_id per People Manager Area
    const filteredItems = [];
    
    for (const item of menuItems) {
      if (item.section) {
        // Special handling for People Manager Area (requires org_role_id = 2)
        if (item.requiresOrgRole !== undefined) {
          if (Number(orgRoleId) === item.requiresOrgRole) {
            filteredItems.push(item);
          }
        } else {
          // Check if user has access to this section based on role_id
          if (hasMenuSectionAccess(userRole, item.section)) {
            filteredItems.push(item);
          }
        }
      } else if (item.separator) {
        // Always include separators if the previous section was included
        if (filteredItems.length > 0) {
          filteredItems.push(item);
        }
      } else if (item.text) {
        // Special handling for People Manager Area items (requires org_role_id = 2)
        if (item.requiresOrgRole !== undefined) {
          if (Number(orgRoleId) === item.requiresOrgRole) {
            filteredItems.push(item);
          }
        } else {
          // Check if user has access to this menu item based on role_id
          if (hasMenuItemAccess(userRole, item.text)) {
            filteredItems.push(item);
          }
        }
      }
    }
    
    return filteredItems;
  };

  // User menu state
  const [anchorEl, setAnchorEl] = useState(null);
  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  // Logout con Cognito
  const handleLogout = () => {
    setAnchorEl(null);
    cognitoLogout();
  };
  // Ricava nome/cognome/iniziali (mantenuto per compatibilità se necessario)
  let userObj = users.find(u => String(u.id) === String(selectedUser));
  let userName = userObj ? (userObj.name || userObj.username || userObj.email || "") : "";
  let userSurname = userObj ? (userObj.surname || "") : "";
  let initials = null;
  if (userName && userSurname) {
    initials = `${userName.trim()[0] || ''}${userSurname.trim()[0] || ''}`.toUpperCase();
  } else if (userName) {
    initials = userName.trim()[0].toUpperCase();
  } else if (userSurname) {
    initials = userName.trim()[0].toUpperCase();
  }
  let isLoggedIn = !!userObj;

  const drawer = (
    <div>
      <DrawerHeader>
        <img src={open ? "/logo.png" : "/logo_small.png"} alt="Logo" style={{ height: 30, margin: 'auto' }} />
        {open && (
          <IconButton onClick={handleDrawerClose} sx={{ color: 'white' }}>
            <ChevronLeftIcon />
          </IconButton>
        )}
      </DrawerHeader>
      <Divider />
      <List>
                {getFilteredMenuItems().map((item, idx) => {
          if (item.section) {
              return (
                <React.Fragment key={item.section}>
                  <Divider sx={{ bgcolor: 'rgba(255,255,255,0.15)', my: 0.5 }} />
                  <ListSubheader 
                    sx={{ 
                      bgcolor: '#333c44', 
                      color: '#b0bec5', 
                      fontWeight: 400, 
                      fontSize: open ? '0.85rem' : 0, 
                      opacity: open ? 1 : 0, 
                      pl: open ? 2 : 0, 
                      transition: 'opacity 0.2s, font-size 0.2s',
                      lineHeight: 1.7
                    }}
                  >
                    {item.section}
                  </ListSubheader>
                </React.Fragment>
              );
            }
          if (item.separator) {
            return (
              <Divider 
                key={`separator-${idx}`}
                sx={{ 
                  bgcolor: 'rgba(255,255,255,0.08)', 
                  mx: 2, 
                  my: 1,
                  opacity: open ? 1 : 0,
                  transition: 'opacity 0.2s'
                }} 
              />
            );
          }
          if (item.text === "Home") {
            return (
              <ListItem key={item.text} disablePadding sx={{ display: 'block' }}>
                <ListItemButton
                  selected={location.pathname === item.path}
                  onClick={() => navigate(item.path)}
                  sx={{
                    minHeight: 48,
                    justifyContent: open ? 'initial' : 'center',
                    px: 2.5,
                    color: 'white',
                    '&.Mui-selected': {
                      bgcolor: 'rgba(0, 0, 0, .1)'
                    }
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: open ? 3 : 0,
                      justifyContent: 'center',
                      color: 'white',
                      '& .MuiSvgIcon-root': {
                        fontSize: '1rem'
                      }
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  {open && (
                    <ListItemText 
                      primary={item.text} 
                      sx={{ fontSize: '0.75rem', fontFamily: 'Exo 2, sans-serif !important' }}
                    />
                  )}
                </ListItemButton>
              </ListItem>
            );
          }
          return (
            <ListItem key={item.text} disablePadding sx={{ display: 'block' }}>
              <ListItemButton
                selected={location.pathname === item.path}
                onClick={() => navigate(item.path)}
                sx={{
                  minHeight: 48,
                  justifyContent: open ? 'initial' : 'center',
                  px: 2.5,
                  color: 'white',
                  '&.Mui-selected': {
                    bgcolor: 'rgba(0, 0, 0, .1)'
                  }
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: open ? 3 : 0,
                    justifyContent: 'center',
                    color: 'white',
                    '& .MuiSvgIcon-root': {
                      fontSize: '1rem'
                    }
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                {open && (
                  <ListItemText 
                    primary={item.text} 
                    sx={{ fontSize: '0.75rem', fontFamily: 'Exo 2, sans-serif !important' }}
                  />
                )}
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
    </div>
  );

  // In development mode, always provide mock data
  const isDev = process.env.NODE_ENV === 'development';
  
  // Force selectedUser to 1 in development mode
  const forcedSelectedUser = isDev ? 1 : selectedUser;
  
  // Ensure we always have valid context values
  const contextValue = { 
    selectedUser: forcedSelectedUser, 
    setSelectedUser, 
    users, 
    cognitoUser: isDev 
      ? { email: 'dev@example.com', name: 'Developer', username: 'devuser', surname: 'User', sub: 'mock-sub-123', groups: ['developers'] }
      : (cognitoUser || null), 
    // In development, usa il backendUser proveniente da useCognito (mock con role_id e org_role_id corretti)
    backendUser: isDev
      ? (backendUser || { id: 3, name: 'Developer', username: 'devuser', email: 'dev@example.com', surname: 'User', role_id: 1, role: 'Admin', org_role_id: 2 })
      : (backendUser || null),
    isAuthenticated: isDev ? true : (isAuthenticated || false), 
    authLoading: isDev ? false : (authLoading || false),
    // In development, non forzare Admin: usa il role_id del backendUser mock
    userRole: isDev ? (backendUser?.role_id || 1) : (backendUser?.role_id || null)
  };

  return (
    <UserContext.Provider value={contextValue}>
                      <Box sx={{ display: "flex" }}>
          <AppBar
            position="fixed"
            sx={{
              bgcolor: '#5ab031',
              transition: 'width 0.2s, margin-left 0.2s',
              ml: { sm: open ? `${drawerWidth}px` : 0 },
              width: { sm: open ? `calc(100% - ${drawerWidth}px)` : '100%' }
            }}
          >
          <Toolbar>
            { !open && (
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerOpen}
                sx={{ mr: 2, ml: { xs: 0, sm: '80px' } }}
              >
                <MenuIcon />
              </IconButton>
            )}
            <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1, textAlign: 'left', marginLeft: '24px' }}>
              RUNTime
            </Typography>

            
            {/* User/Login menu */}
            {isAuthenticated && cognitoUser ? (
              <>
                <IconButton onClick={handleMenuOpen} sx={{ ml: 2 }}>
                  <Avatar sx={{ bgcolor: '#333c44', width: 36, height: 36 }}>
                    {(() => {
                      // Genera iniziali dal backendUser
                      if (backendUser) {
                        const name = backendUser.name || backendUser.username || '';
                        const surname = backendUser.surname || '';
                        
                        if (name && surname) {
                          return `${name.trim()[0] || ''}${surname.trim()[0] || ''}`.toUpperCase();
                        } else if (name) {
                          return name.trim()[0].toUpperCase();
                        } else if (surname) {
                          return surname.trim()[0].toUpperCase();
                        }
                      }
                      
                      // Fallback all'icona generica
                      return <AccountCircle sx={{ color: 'white', width: 28, height: 28 }} />;
                    })()}
                  </Avatar>
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleMenuClose}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                  {/* Indicatore stato autenticazione */}
                  {isAuthenticated && cognitoUser && (
                    <MenuItem disabled sx={{ 
                      bgcolor: backendUser ? 'success.main' : 'error.main', 
                      color: 'white',
                      fontSize: '0.75rem',
                      fontWeight: 'bold',
                      display: 'flex',
                      alignItems: 'center',
                      opacity: 1,
                      gap: 0.5
                    }}>
                      {cognitoUser.email}
                      {!backendUser && ' (Not in DB)'}
                    </MenuItem>
                  )}
                  {authLoading && (
                    <MenuItem disabled sx={{ 
                      bgcolor: 'warning.main', 
                      color: 'white',
                      fontSize: '0.75rem',
                      fontWeight: 'bold',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5
                    }}>
                      🔄 Checking auth...
                    </MenuItem>
                  )}
                  {!isAuthenticated && !authLoading && (
                    <MenuItem disabled sx={{ 
                      bgcolor: 'error.main', 
                      color: 'white',
                      fontSize: '0.75rem',
                      fontWeight: 'bold',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5
                    }}>
                      ❌ Not authenticated
                    </MenuItem>
                  )}
                  
                  <Divider />
                  <MenuItem onClick={() => { handleMenuClose(); navigate('/profile'); }}>
                    {backendUser ? (backendUser.name || backendUser.username || backendUser.email) : (cognitoUser.name || cognitoUser.email)}
                  </MenuItem>
                  {backendUser ? (
                    <MenuItem disabled sx={{ 
                      fontSize: '0.75rem', 
                      color: 'text.secondary',
                      fontStyle: 'italic'
                    }}>
                      Role: {getRoleDisplayName(backendUser.role_id)}
                    </MenuItem>
                  ) : (
                    <MenuItem disabled sx={{ 
                      fontSize: '0.75rem', 
                      color: 'error.main',
                      fontStyle: 'italic'
                    }}>
                      User not found in database
                    </MenuItem>
                  )}
                  <MenuItem onClick={() => { handleMenuClose(); navigate('/logout'); }}>
                    Logout
                  </MenuItem>
                </Menu>
              </>
            ) : (
              <Button color="inherit" sx={{ ml: 2, fontWeight: 700 }} onClick={() => navigate('/login')}>
                Login
              </Button>
            )}
          </Toolbar>
        </AppBar>
        <Box
          component="nav"
          sx={{ width: { sm: open ? drawerWidth : 0 }, flexShrink: { sm: 0 } }}
        >
          <Drawer
            variant="temporary"
            open={open}
            onClose={handleDrawerClose}
            ModalProps={{
              keepMounted: true,
            }}
            sx={{
              display: { xs: "block", sm: "none" },
              "& .MuiDrawer-paper": {
                boxSizing: "border-box",
                width: drawerWidth,
                bgcolor: '#333c44'
              },
            }}
          >
            {drawer}
          </Drawer>
          <Drawer
            variant="permanent"
            open={open}
            sx={{
              display: { xs: "none", sm: "block" },
              "& .MuiDrawer-paper": {
                boxSizing: "border-box",
                width: open ? drawerWidth : 73,
                transition: 'width 0.2s',
                overflowX: 'hidden',
                bgcolor: '#333c44'
              },
            }}
          >
            {drawer}
          </Drawer>
        </Box>
        <Box component="main" sx={{ 
          flexGrow: 1, 
          p: 3, 
          
          ml: { sm: open ? `10px` : '73px' }, // margine sinistro dinamico
          transition: 'margin-left 0.2s',
          height: '100vh',
          overflowY: 'auto',
          overflowX: 'hidden'
        }}>
          <DrawerHeader />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/" element={<ProtectedRoute><Home /></ProtectedRoute>} />
            <Route path="/list-tracking-view" element={<ProtectedRoute><TrackingListManagerView /></ProtectedRoute>} />
            <Route path="/daily-tracking" element={<ProtectedRoute><DailyTracking /></ProtectedRoute>} />
            <Route path="/monthly-user-view" element={<ProtectedRoute><MonthlyUserView /></ProtectedRoute>} />
            <Route path="/tracking-list-user" element={<ProtectedRoute><TrackingListUserView /></ProtectedRoute>} />
            <Route path="/monthly-mng-view" element={<ProtectedRoute><MonthlyManagerView /></ProtectedRoute>} />
            <Route path="/monthly-mng-view/project/:projectId" element={<ProtectedRoute><MonthlyProjectDetail /></ProtectedRoute>} />
            <Route
              path="/monthly-people-mng-view"
              element={
                <ProtectedRoute requiredOrgRole={2}>
                  <MonthlyPeopleManagerView />
                </ProtectedRoute>
              }
            />
            <Route
              path="/monthly-people-mng-view/user/:userId"
              element={
                <ProtectedRoute requiredOrgRole={2}>
                  <MonthlyPeopleManagerDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/list-tracking-people-mng-view"
              element={
                <ProtectedRoute requiredOrgRole={2}>
                  <TrackingListPeopleManagerView />
                </ProtectedRoute>
              }
            />
            <Route path="/project-reporting" element={<ProtectedRoute><ProjectReporting /></ProtectedRoute>} />
            <Route path="/customers" element={<ProtectedRoute><Customers /></ProtectedRoute>} />
            <Route path="/projects" element={<ProtectedRoute><Projects /></ProtectedRoute>} />
            <Route path="/projects/:id" element={<ProtectedRoute><ProjectDetail /></ProtectedRoute>} />
            <Route path="/users" element={<ProtectedRoute><Users /></ProtectedRoute>} />
            <Route path="/users/:id" element={<ProtectedRoute><UserDetail /></ProtectedRoute>} />
            <Route path="/activities" element={<ProtectedRoute><Activities /></ProtectedRoute>} />
            <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
            <Route path="*" element={<NotFound />} />
          </Routes>
          {rightOpen && (
            <Box
              sx={{
                width: 320,
                height: '100vh',
                position: 'fixed',
                right: 0,
                top: 0,
                bgcolor: 'white',
                borderLeft: '1px solid #ddd',
                zIndex: 1200,
                boxShadow: 3,
                p: 2
              }}
            >
              <RightSection />
            </Box>
          )}
        </Box>
      </Box>
    </UserContext.Provider>
  );
}